import React from 'react';
import { Button, DatePicker, Input, Radio, Select, Typography } from '@crisellevaldez/speculo-ui';
import {
  Calendar,
  ChevronDown,
  Hash,
  Info,
  List,
  Mail,
  Phone,
  SlidersHorizontal,
  Tag,
  ToggleLeft,
  Type,
  User,
  X,
} from 'lucide-react';
import { SearchableSelect } from '../../common/SearchableSelect';
import { Tooltip } from '../../common/Tooltip';
import { useContactFilterFields } from '../../../hooks/graphql/contacts/useContactFilterFields';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { buildUserOptions } from '../../../utils/userOptions';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import {
  dayBoundISO,
  isoToDateLabel,
  operatorChangePatch,
  parseStoredDate,
} from './advancedFilterDates';
import {
  LIST_OPERATORS,
  NULLARY_OPERATORS,
  isConditionComplete,
  operatorLabel,
  valueOptions,
  visibleOperators,
  type ContactAdvancedFilter,
  type ContactFilterCombinator,
  type ContactFilterCondition,
  type ContactFilterField,
  type ContactFilterOperator,
} from '../../../types/contactFilter.types';

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const COMBINATOR_OPTIONS = [
  { value: 'AND', label: 'all' },
  { value: 'OR', label: 'any' },
];

type OptionList = { value: string; label: string }[];

/** A glyph per field, so a card is identifiable before reading its title. */
/**
 * Help text for fields whose names do not separate them on their own.
 *
 * `leadSource` and `leadSourceSecondary` read as the same thing but come from
 * different columns and answer different questions, and their labels ("Source"
 * and "Lead Source") do not say which is which.
 */
const FIELD_HINTS: Record<string, string> = {
  leadSource:
    'Where this contact originated — e.g., CRM sync, manual entry, database upload, or website form.',
  leadSourceSecondary:
    'Which campaign channel produced this lead — e.g., Email, PPC, Organic, or Database.',
};

const fieldIcon = (field: ContactFilterField) => {
  if (field.name === 'email') return Mail;
  if (field.name === 'phone') return Phone;
  if (field.name === 'assignedAgentId') return User;
  if (field.dataType === 'TAGS') return Tag;
  if (field.dataType === 'NUMBER') return Hash;
  if (field.dataType === 'DATETIME') return Calendar;
  if (field.dataType === 'BOOLEAN') return ToggleLeft;
  if (field.dataType === 'ENUM') return List;
  return Type;
};

/** Render one operand the way the user picked it, not the way it is stored. */
const describeValue = (
  condition: ContactFilterCondition,
  field: ContactFilterField | undefined,
  lookups: { tagOptions: OptionList; userOptions: OptionList; timezone: string }
): string => {
  const label = (raw: string): string => {
    if (field?.dataType === 'DATETIME') return isoToDateLabel(raw, lookups.timezone);
    if (field?.dataType === 'BOOLEAN') return raw === 'true' ? 'Yes' : 'No';
    const source =
      field?.name === 'assignedAgentId'
        ? lookups.userOptions
        : field?.dataType === 'TAGS'
          ? lookups.tagOptions
          : valueOptions(field);
    return source.find((option) => option.value === raw)?.label ?? raw;
  };

  if (LIST_OPERATORS.has(condition.operator)) {
    return (condition.values ?? []).map(label).join(', ');
  }
  return label(condition.value ?? '');
};

/**
 * One readable phrase per complete condition, in card order.
 *
 * Returns the phrases rather than a joined sentence so the caller can render
 * the and/or between them as its own element. Derived on every render, so it
 * cannot drift from the conditions.
 */
const describeConditions = (
  filter: ContactAdvancedFilter,
  fieldsByName: Map<string, ContactFilterField>,
  lookups: { tagOptions: OptionList; userOptions: OptionList; timezone: string }
): string[] =>
  filter.conditions
    .filter(isConditionComplete)
    // A field gated off for the org has its card dropped below; describing it
    // here anyway would print the raw field name in the summary.
    .filter((condition) => fieldsByName.has(condition.field))
    .map((condition) => {
      const field = fieldsByName.get(condition.field);
      const name = field?.label ?? condition.field;
      const operator = operatorLabel(condition.operator, field);
      if (NULLARY_OPERATORS.has(condition.operator)) return `${name} ${operator}`;
      return `${name} ${operator} ${describeValue(condition, field, lookups)}`;
    });

interface PickerSources {
  tagOptions: OptionList;
  tagsLoading: boolean;
  tagsHasMore: boolean;
  onTagsLoadMore: () => void;
  onTagsSearch: (query: string) => void;
  userOptions: OptionList;
  usersLoading: boolean;
  /** Org timezone — the basis every date bound is built and read in. */
  timezone: string;
}

interface ValueEditorProps extends PickerSources {
  field: ContactFilterField;
  condition: ContactFilterCondition;
  onChange: (patch: Partial<ContactFilterCondition>) => void;
}

/**
 * The operand widget for one condition, chosen from the field's data type and
 * the operator. Enum options come from the server catalogue, and tag names and
 * team members from their own queries, so the values on offer always exist.
 */
const ValueEditor: React.FC<ValueEditorProps> = ({
  field,
  condition,
  onChange,
  tagOptions,
  tagsLoading,
  tagsHasMore,
  onTagsLoadMore,
  onTagsSearch,
  userOptions,
  usersLoading,
  timezone,
}) => {
  if (field.dataType === 'TAGS') {
    return (
      <SearchableSelect
        className="w-full"
        multiple
        placeholder="Select tags"
        searchPlaceholder="Search tags..."
        value={condition.values ?? []}
        onChange={(values) => onChange({ values })}
        options={tagOptions}
        isLoading={tagsLoading}
        hasMore={tagsHasMore}
        onLoadMore={onTagsLoadMore}
        onSearch={onTagsSearch}
      />
    );
  }

  // assignedAgentId holds a user id. The server types it STRING, and the
  // catalogue has no way to say "populate this from the org users query", so
  // the picker is matched on the field name — otherwise it renders as a text
  // box asking someone to type a UUID.
  if (field.name === 'assignedAgentId') {
    return (
      <SearchableSelect
        className="w-full"
        multiple
        placeholder="Select team members"
        searchPlaceholder="Search team..."
        value={condition.values ?? []}
        onChange={(values) => onChange({ values })}
        options={userOptions}
        isLoading={usersLoading}
      />
    );
  }

  if (field.dataType === 'ENUM' && field.options.length > 0) {
    return (
      <Select
        className="w-full"
        multiple
        searchable
        placeholder="Select values"
        value={condition.values ?? []}
        onChange={(value) => onChange({ values: value as string[] })}
        options={valueOptions(field)}
      />
    );
  }

  if (field.dataType === 'BOOLEAN') {
    return (
      <Select
        className="w-full"
        placeholder="Select"
        value={condition.value ?? ''}
        onChange={(value) => onChange({ value: value as string })}
        options={BOOLEAN_OPTIONS}
      />
    );
  }

  if (field.dataType === 'DATETIME') {
    return (
      <DatePicker
        className="w-full"
        placeholder="Select a date"
        value={parseStoredDate(condition.value, timezone)}
        onChange={(date) =>
          onChange({ value: date ? dayBoundISO(date, condition.operator, timezone) : '' })
        }
      />
    );
  }

  if (field.dataType === 'NUMBER') {
    return (
      <Input
        className="w-full"
        type="number"
        step="1"
        placeholder="Enter a number"
        value={condition.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }

  return (
    <Input
      className="w-full"
      placeholder="Enter a value"
      value={condition.value ?? ''}
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
};

interface FieldFilterProps extends PickerSources {
  field: ContactFilterField;
  condition: ContactFilterCondition;
  onChange: (patch: Partial<ContactFilterCondition>) => void;
  onRemove: () => void;
}

/**
 * One field's filter: the operators it accepts as a radio list, with the value
 * input directly under whichever is selected.
 *
 * A radio group rather than a dropdown because the choices are the point —
 * seeing that a field can be matched, negated, or checked for emptiness is
 * most of what makes the filter discoverable. It also means one condition per
 * field, which is why the field picker hides fields already added.
 *
 * Separated by a rule rather than boxed: a stack of bordered cards inside an
 * already-bordered panel is three nested frames deep.
 */
const FieldFilter: React.FC<FieldFilterProps> = ({
  field,
  condition,
  onChange,
  onRemove,
  ...pickers
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const Icon = fieldIcon(field);

  const handleOperatorChange = (operator: ContactFilterOperator) =>
    onChange(
      operatorChangePatch(condition, operator, field.dataType === 'DATETIME', pickers.timezone)
    );

  return (
    <div className="border-b border-foreground/25 pb-5 pt-1 last:border-b-0 last:pb-0">
      <div className="flex items-center gap-2 py-1.5">
        <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/60" />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <Typography variant="small" className="min-w-0 truncate font-medium">
            {field.label}
          </Typography>
          {FIELD_HINTS[field.name] && (
            <Tooltip content={FIELD_HINTS[field.name]} position="top">
              <Info
                tabIndex={0}
                role="note"
                aria-label={`About ${field.label}: ${FIELD_HINTS[field.name]}`}
                className="h-3 w-3 cursor-help text-muted-foreground hover:text-foreground"
              />
            </Tooltip>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((wasCollapsed) => !wasCollapsed)}
          aria-label={collapsed ? `Expand ${field.label}` : `Collapse ${field.label}`}
          aria-expanded={!collapsed}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${field.label} filter`}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-2.5 pt-2">
          {visibleOperators(field).map((operator) => {
            const selected = condition.operator === operator;
            return (
              <div key={operator}>
                <Radio
                  id={`${field.name}-${operator}`}
                  name={`operator-${field.name}`}
                  value={operator}
                  checked={selected}
                  onChange={() => handleOperatorChange(operator)}
                  label={operatorLabel(operator, field)}
                />
                {/* The operand belongs to the operator, so it sits under the
                    selected radio rather than in a shared slot at the bottom. */}
                {selected && !NULLARY_OPERATORS.has(operator) && (
                  <div className="mt-1.5 pl-6">
                    <ValueEditor
                      field={field}
                      condition={condition}
                      onChange={onChange}
                      {...pickers}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface AdvancedFilterBuilderProps {
  value: ContactAdvancedFilter;
  onChange: (filter: ContactAdvancedFilter) => void;
  /**
   * How many filters are set on the other tab.
   *
   * The summary describes these conditions only, but the two tabs are ANDed
   * server-side — so without saying so it claims to explain a result set it
   * only half accounts for.
   */
  quickFilterCount?: number;
  /** Gates the catalogue and picker queries to when the panel is actually open. */
  enabled?: boolean;
  organizationId?: string;
}

/**
 * Builds contact filter conditions as a stack of per-field cards: search for a
 * field, get a card, pick its operator, fill in the value.
 *
 * One condition per field, so the picker offers only fields not already added.
 * The filter is sent to the GraphQL resolver and evaluated in Spanner; nothing
 * here narrows the rows locally, so totals and paging stay correct.
 */
export const AdvancedFilterBuilder: React.FC<AdvancedFilterBuilderProps> = ({
  value,
  onChange,
  quickFilterCount = 0,
  enabled = true,
  organizationId,
}) => {
  const {
    groups: catalogueGroups,
    fieldsByName,
    isLoading: fieldsLoading,
  } = useContactFilterFields(organizationId, enabled);

  const {
    data: tags = [],
    isLoading: tagsLoading,
    hasMore: tagsHasMore,
    loadMore: tagsLoadMore,
    setSearch: tagsSetSearch,
  } = useUniqueContactTags(enabled);

  const tagOptions = React.useMemo(() => tags.map((tag) => ({ value: tag, label: tag })), [tags]);

  const { organization } = useOrganizationStore();
  const timezone = organization?.timezone || 'UTC';

  const { users, loading: usersLoading } = useOrganizationUsers();
  const userOptions = React.useMemo(() => buildUserOptions(users), [users]);

  // Option sources the catalogue cannot describe: tag names and org users both
  // come from their own queries rather than the field's static `options`.
  const pickers: PickerSources = {
    tagOptions,
    tagsLoading,
    tagsHasMore,
    onTagsLoadMore: tagsLoadMore,
    onTagsSearch: tagsSetSearch,
    userOptions,
    usersLoading,
    timezone,
  };

  const usedFields = new Set(value.conditions.map((condition) => condition.field));

  // One condition per field, so a field already on the stack is not offered
  // again. The catalogue's grouping is folded into the label since the picker
  // takes a flat list, and its search does the rest.
  const addableFields = React.useMemo(
    () =>
      catalogueGroups.flatMap((group) =>
        group.fields
          .filter((field) => !usedFields.has(field.name))
          .map((field) => ({
            value: field.name,
            label: `${group.label} · ${field.label}`,
            description: FIELD_HINTS[field.name],
          }))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogueGroups, value.conditions]
  );

  const addField = (name: string) => {
    const field = fieldsByName.get(name);
    if (!field) return;
    onChange({
      ...value,
      conditions: [
        ...value.conditions,
        {
          field: name,
          operator: visibleOperators(field)[0] ?? 'EQ',
          value: null,
          values: null,
        },
      ],
    });
  };

  const patchCondition = (at: number, patch: Partial<ContactFilterCondition>) =>
    onChange({
      ...value,
      conditions: value.conditions.map((c, i) => (i === at ? { ...c, ...patch } : c)),
    });

  const removeCondition = (at: number) =>
    onChange({ ...value, conditions: value.conditions.filter((_, i) => i !== at) });

  const summaryParts = describeConditions(value, fieldsByName, {
    tagOptions,
    userOptions,
    timezone,
  });
  const joinerWord = value.combinator === 'AND' ? 'and' : 'or';

  return (
    <div className="space-y-5">
      {(summaryParts.length > 0 || quickFilterCount > 0) && (
        /* The sentence people read to check the filter before applying it. The
           tint keeps it findable without the border-plus-accent-bar treatment,
           which read as a warning banner. Tokens, so both themes hold. */
        <div className="flex items-start gap-2 rounded-lg bg-primary/10 px-3 py-2">
          <SlidersHorizontal className="mt-[3px] h-4 w-4 shrink-0 text-foreground/70" />
          <div className="min-w-0">
            {summaryParts.length > 0 && (
              <p className="text-sm leading-relaxed text-foreground/70">
                Showing contacts where{' '}
                {summaryParts.map((part, at) => (
                  <React.Fragment key={at}>
                    {at > 0 && (
                      <>
                        {/* The joiner is the one word that changes what the whole
                        filter means, so it is the only coloured text here.
                        amber-600 / primaryfortext is the app's existing pair
                        for amber text — plain text-primary is amber at 61%
                        lightness and unreadable on its own tint in light mode. */}
                        <span className="font-bold text-amber-600 dark:text-primaryfortext">
                          {joinerWord}
                        </span>{' '}
                      </>
                    )}
                    <span className="font-semibold text-foreground">{part}</span>{' '}
                  </React.Fragment>
                ))}
              </p>
            )}
            {quickFilterCount > 0 && (
              /* Named rather than folded into the sentence: these live on the
                 other tab, so the point is that something off-screen is also
                 narrowing the list. */
              <p className={`text-xs text-foreground/60 ${summaryParts.length > 0 ? 'mt-1' : ''}`}>
                {quickFilterCount} quick {quickFilterCount === 1 ? 'filter' : 'filters'}{' '}
                {quickFilterCount === 1 ? 'is' : 'are'} also applied.
              </p>
            )}
          </div>
        </div>
      )}

      {value.conditions.length > 1 && (
        <div className="flex items-center gap-2">
          <Typography variant="small" className="shrink-0 text-muted-foreground">
            Match
          </Typography>
          {/* Sized by a wrapper, not by Select's className: Select puts the
              passed class on its inner trigger and keeps w-full on its own
              wrapper, so a width passed directly leaves the wrapper full-width
              and it eats the rest of the row. */}
          <div className="w-[7rem] shrink-0">
            <Select
              className="w-full"
              value={value.combinator}
              onChange={(next) =>
                onChange({ ...value, combinator: next as ContactFilterCombinator })
              }
              options={COMBINATOR_OPTIONS}
            />
          </div>
          <Typography variant="small" className="shrink-0 text-muted-foreground">
            of these
          </Typography>
        </div>
      )}

      {/* Value is left empty so the box reads as an action rather than a
          selection: picking a field adds a card and clears the input. */}
      <SearchableSelect
        className="w-full"
        placeholder="Add a filter"
        searchPlaceholder="Search fields..."
        emptyText={fieldsLoading ? 'Loading fields...' : 'No fields left to add'}
        value=""
        onChange={addField}
        options={addableFields}
        isLoading={fieldsLoading}
      />

      {value.conditions.map((condition, at) => {
        const field = fieldsByName.get(condition.field);
        // A condition can outlive its catalogue entry — a field gated off for
        // the org, say. Drop the card rather than rendering a broken one.
        if (!field) return null;
        return (
          <FieldFilter
            key={condition.field}
            field={field}
            condition={condition}
            onChange={(patch) => patchCondition(at, patch)}
            onRemove={() => removeCondition(at)}
            {...pickers}
          />
        );
      })}

      {value.conditions.length > 0 && (
        <div className="flex justify-center pt-1">
          <Button variant="ghost" size="sm" onClick={() => onChange({ ...value, conditions: [] })}>
            Clear conditions
          </Button>
        </div>
      )}
    </div>
  );
};
